/* const BUY_BOX_SELECTOR = 'product-info .product-form__buttons'; */
/**
 * Cart drawer — owns its open/close state (no Alpine) and re-renders its
 * wrapper from the section HTML that cart.js bundles into every cart mutation
 * ('cart:change' event). Rebuy's Smart Cart is the cart shoppers see (after an
 * add and from the header, as on live), so this drawer only opens from the
 * header when Rebuy hasn't started.
 * @extends HTMLElement
 */
export class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.onCartChange = this.onCartChange.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.addEventListener('click', this.onInnerClick);
  }

  connectedCallback() {
    document.addEventListener('cart:change', this.onCartChange);
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this.onCartChange);
    document.removeEventListener('click', this.onDocumentClick);
    clearInterval(this.rebuyWaitTimer);
    this.rebuyWaitTimer = null;
  }

  get sectionId() {
    return this.closest('.shopify-section')?.id.replace('shopify-section-', '');
  }

  open() {
    this.classList.add('cart-open');
  }

  close() {
    if (!this.classList.contains('cart-open')) return;
    this.classList.remove('cart-open');
    document.dispatchEvent(new CustomEvent('cart:close'));
  }

  toggle() {
    if (this.classList.contains('cart-open')) {
      this.close();
    } else {
      this.open();
    }
  }

  onDocumentClick(event) {
    const bubble = event.target.closest('#header-cart-bubble');
    if (!bubble) return;
    event.preventDefault();

    if (this.classList.contains('cart-open')) {
      this.close();
      return;
    }

    // Live: the header cart opens Rebuy's Smart Cart. The theme drawer is only the fallback when Rebuy doesn't start (~3s).
    if (this.rebuyCartReady()) {
      window.Rebuy.SmartCart.show();
      return;
    }
    this.showRebuyCartWhenReady(12, () => this.open());
  }

  rebuyCartReady() {
    return typeof window.Rebuy?.SmartCart?.show === 'function' && !!document.querySelector('.rebuy-cart');
  }

  onInnerClick(event) {
    if (event.target.closest('.drawer-overlay, .cart-drawer__close')) {
      this.close();
      return;
    }

    const noteLabel = event.target.closest('.cart-note label');
    if (noteLabel) noteLabel.classList.toggle('note-open');
  }

  onCartChange(event) {
    const html = event.detail.sections?.[this.sectionId];
    if (html) this.render(html, event.detail.action);

    if (event.detail.action !== 'add') return;

    // SSO restructure: Rebuy loads after first interaction, so an early add waits for it to open the flyout
    if (!window.Rebuy?.SmartCart) this.showRebuyCartWhenReady();

    /* Rebuy owns the add-to-cart flyout — do not open the theme drawer.
    const picker = event.detail.source?.closest?.('.card-quick-add__modal');
    if (picker && window.matchMedia('(max-width: 768px)').matches) {
      this.open();
      return;
    }

    if (event.detail.source?.closest?.(BUY_BOX_SELECTOR)) {
      this.open();
      return;
    }

    this.open();
    */
  }

  /** Polls every 250ms (default max ~20s) until Rebuy's Smart Cart has rendered (.rebuy-cart), then opens it
      with the updated cart; onTimeout runs if it never does. */
  showRebuyCartWhenReady(maxAttempts = 80, onTimeout) {
    if (this.rebuyWaitTimer) return;

    let attempts = 0;
    this.rebuyWaitTimer = setInterval(() => {
      attempts += 1;
      const ready = this.rebuyCartReady();
      if (!ready && attempts < maxAttempts) return;

      clearInterval(this.rebuyWaitTimer);
      this.rebuyWaitTimer = null;
      if (ready) window.Rebuy.SmartCart.show();
      else onTimeout?.();
    }, 250);
  }

  render(html, action) {
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('#cart-drawer .drawer__wrapper');
    const current = this.querySelector('.drawer__wrapper');
    if (!next || !current) return;

    const scrollTop = current.querySelector('.cart-items')?.scrollTop || 0;
    current.innerHTML = next.innerHTML;

    const items = current.querySelector('.cart-items');
    if (items) items.scrollTop = action === 'add' ? 0 : scrollTop;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}